import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
	type AllowedRole,
	getHomeByRole,
	resolveAccessDecision,
} from "../../core/auth/route-access";
import { useAuthSessionContext } from "./auth-session-store";

export type ProtectedRouteProps = {
	allowedRoles?: readonly AllowedRole[];
	redirectToLogin?: string;
	redirectUnauthorizedTo?: string;
	children?: ReactNode;
};

export function ProtectedRoute({
	allowedRoles,
	redirectToLogin = "/login",
	redirectUnauthorizedTo,
	children,
}: ProtectedRouteProps) {
	const location = useLocation();
	const isAdministrativePath = location.pathname.startsWith("/admin");
	const resolvedRedirectToLogin =
		redirectToLogin === "/login" && isAdministrativePath
			? "/_internal/admin-access"
			: redirectToLogin;
	const { session, role, isLoading } = useAuthSessionContext();

	if (isLoading) return null;

	const decision = resolveAccessDecision(session, allowedRoles);

	if (decision === "allow") {
		return children ? <>{children}</> : <Outlet />;
	}

	if (decision === "unauthenticated") {
		return (
			<Navigate
				to={resolvedRedirectToLogin}
				replace
				state={{ redirectTo: location.pathname + location.search }}
			/>
		);
	}

	const fallback = redirectUnauthorizedTo ?? getHomeByRole(role);
	return <Navigate to={fallback} replace />;
}
